/**
 * Who can be @mentioned in an update: the account's users and teams.
 *
 * Loaded once, lazily — the first time a mention picker opens — through
 * lib/mondayApi, and again at apply time to drop mentions of people who left
 * (see serializeUpdate's `known`). Needs the users:read and teams:read scopes;
 * a missing scope is named in `error`, never swallowed, so the picker can say
 * why it is empty instead of showing "No people".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { describeApiErrors } from './useBoardColumns';

export const USERS_QUERY = 'query { users (limit: 200, kind: non_guests) { id name } }';
export const TEAMS_QUERY = 'query { teams { id name } }';

async function ask(api, query) {
  let res;
  try {
    res = await api.api(query);
  } catch (e) {
    return { error: (e && e.message) || 'request failed' };
  }
  const error = describeApiErrors(res);
  if (error) return { error };
  return { data: res && res.data };
}

/** @returns {{ users: Array, teams: Array, error: string|null }} */
export async function fetchAccountPeople(api) {
  const [u, t] = await Promise.all([ask(api, USERS_QUERY), ask(api, TEAMS_QUERY)]);
  const users = (u.data && u.data.users) || [];
  const teams = (t.data && t.data.teams) || [];
  const error = u.error || t.error || null;
  if (error) console.error('Could not load the account people for mentions:', error);
  return { users, teams, error };
}

/** Keys for serializeUpdate's `known`: "User:1", "Team:2". */
export function knownMentionKeys({ users = [], teams = [] }) {
  return new Set([...users.map((u) => `User:${u.id}`), ...teams.map((t) => `Team:${t.id}`)]);
}

/**
 * useAccountPeople(api) → { status, users, teams, error, load }
 * status: 'idle' | 'loading' | 'loaded'. `load()` is a no-op while a fetch is in
 * flight or once people are in hand; a fetch that ended empty is retried.
 */
export function useAccountPeople(api) {
  const [state, setState] = useState({ status: 'idle', users: [], teams: [], error: null });
  const generation = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setState({ status: 'idle', users: [], teams: [], error: null });
    return () => {
      generation.current += 1;
    };
  }, [api]);

  const loaded = state.status === 'loaded' && (state.users.length > 0 || state.teams.length > 0);
  const load = useCallback(() => {
    if (inFlight.current || loaded) return;
    const gen = generation.current;
    inFlight.current = true;
    setState((prev) => ({ ...prev, status: 'loading' }));
    fetchAccountPeople(api)
      .catch((e) => ({ users: [], teams: [], error: (e && e.message) || 'request failed' }))
      .then(({ users, teams, error }) => {
        if (gen !== generation.current) return;
        inFlight.current = false;
        setState({ status: 'loaded', users, teams, error });
      });
  }, [api, loaded]);

  return { ...state, load };
}
